import React, { useEffect, useState } from "react";
import { Modal, Input, Select, Checkbox, Button, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { CONTACT_METHODS, CONSENT } from "./constants";
import { Field } from "../onboarding/_ui";

export function ContactForm({ contact, onClose, onSaved }: { contact?: any; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    first_name: contact?.first_name ?? "", last_name: contact?.last_name ?? "",
    phone: contact?.phone ?? "", email: contact?.email ?? "",
    preferred_contact_method: contact?.preferred_contact_method ?? "phone",
    service_address: contact?.service_address ?? "", timezone: contact?.timezone ?? "",
    sms_consent_status: contact?.sms_consent_status ?? "unknown", call_consent_status: contact?.call_consent_status ?? "unknown",
    do_not_call: contact?.do_not_call ?? false, do_not_text: contact?.do_not_text ?? false,
  });
  const [dups, setDups] = useState<Record<string, unknown>[]>([]);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (contact || (!form.phone && !form.email)) { setDups([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.crmCheckDuplicate({ phone: form.phone, email: form.email }); setDups(r.possible_duplicates); } catch { /* */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.phone, form.email, contact]);

  const save = async () => {
    if (form.first_name.trim().length < 1) { toast.error("First name is required."); return; }
    setSaving(true);
    try {
      const body: Record<string, unknown> = { ...form, last_name: form.last_name || null, phone: form.phone || null, email: form.email || null, service_address: form.service_address || null, timezone: form.timezone || null };
      if (contact) await api.crmContactUpdate(contact.id, body); else await api.crmContactCreate(body);
      toast.success("Contact saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={contact ? "Edit contact" : "New contact"} description="Contact details and consent." testid="contact-form">
      <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" required testid="field-con-fn"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="con-first-name" /></Field>
          <Field label="Last name" testid="field-con-ln"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="con-last-name" /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Phone" testid="field-con-phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="con-phone" /></Field>
          <Field label="Email" testid="field-con-email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="con-email" /></Field>
        </div>
        {dups.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="con-dup-warning">
            <p className="text-sm text-amber-800">{dups.length} possible duplicate{dups.length > 1 ? "s" : ""} (matching phone/email). Not merged automatically.</p>
          </div>
        )}
        <Field label="Service address" testid="field-con-addr"><Input value={form.service_address} onChange={(e) => set("service_address", e.target.value)} data-testid="con-address" /></Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Preferred method" testid="field-con-method"><Select value={form.preferred_contact_method} onChange={(v) => set("preferred_contact_method", v)} options={CONTACT_METHODS} testid="con-method" /></Field>
          <Field label="SMS consent" testid="field-con-sms"><Select value={form.sms_consent_status} onChange={(v) => set("sms_consent_status", v)} options={CONSENT} testid="con-sms-consent" /></Field>
          <Field label="Call consent" testid="field-con-call"><Select value={form.call_consent_status} onChange={(v) => set("call_consent_status", v)} options={CONSENT} testid="con-call-consent" /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3"><Checkbox checked={form.do_not_call} onChange={(v) => set("do_not_call", v)} testid="con-dnc" /><span className="text-sm text-slate-700">Do not call</span></label>
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 p-3"><Checkbox checked={form.do_not_text} onChange={(v) => set("do_not_text", v)} testid="con-dnt" /><span className="text-sm text-slate-700">Do not text</span></label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="con-save">{saving ? "Saving…" : "Save contact"}</Button>
        </div>
      </div>
    </Modal>
  );
}
